import { t } from 'i18next'
import React, { ReactNode, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import styled from 'styled-components'
import { useCanBlockNumber } from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import { Header } from '~/components/compositional/DetailViewStyles'
import { Edit, NewCall } from '~/components/compositional/QuickActions'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import MangoPlusAds from '~/components/MangoPlusAds'
import { EmptyDetailView } from '~/components/shared/EmptyDetailView'
import { ToggleBlockedNumberButton } from '~/components/specialized/BlockedNumber/ToggleBlockedNumberButton'
import { ApplicationRoutes } from '~/hooks/useRouting'
import MessageThreadListView from '~/pages/Messaging/MessageThreadsListView'
import MessageThreadView, {
  MessageThreadHeader,
} from '~/pages/Messaging/MessageThreadView'
import { getValidNumber, phoneFormatter } from '~/utils/formatter'
import { showErrorToast } from '~/utils/toast'
import { useMessageGroupsViewModel } from '~/viewModels/Messaging/MessageGroupsViewModel'
import { useMessageThreadsViewModel } from '~/viewModels/Messaging/MessageThreadsViewModel'

import { MessageGroup, MessageThread } from '@mangovoice/types'

const MessageThreadViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

const MessagingPage = () => {
  const {
    callModal: [, setCallModal],
  } = useModalState()
  const { messagingEnabled } = useUserState()
  const [selectedThread, setSelectedThread] = useState<MessageThread>()
  const [selectedMessageGroup, setSelectedMessageGroup] =
    useState<MessageGroup>()
  const [{ isEditing, newThreadName }, setEditThreadForm] = useState({
    isEditing: false,
    newThreadName: selectedThread?.name,
  })
  const canBlockNumber = useCanBlockNumber(
    selectedThread?.external_phone_number,
  )
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { messageThreads } = useMessageThreadsViewModel()
  const { userMessageGroups } = useMessageGroupsViewModel()

  useTogglePanel({ isDisabled: true })

  const { renameThread } = useMessageThreadsViewModel()

  useEffect(() => {
    setEditThreadForm({
      isEditing: false,
      newThreadName: selectedThread?.name,
    })
  }, [selectedThread?.name])

  useEffect(
    function renavigateQuietly() {
      const threadId = searchParams.get('threadId')
      if (threadId) {
        const allThreads = messageThreads.messageThreadsByGroup.get(0) || []
        const thread = allThreads.find((t) => t.id === parseInt(threadId))
        if (thread) {
          handleMessageThreadSelected(
            thread,
            userMessageGroups?.find((g) => g.id === thread.message_group_id),
          )
          navigate(ApplicationRoutes.MESSAGING, { replace: true })
        }
      }
    },
    [searchParams, messageThreads, userMessageGroups],
  )

  const getSubheadingText = () => {
    if (selectedMessageGroup) {
      return t('messagingPage.subheadingText', {
        phoneNumber: phoneFormatter(selectedThread?.external_phone_number),
      })
    }
    return `${phoneFormatter(selectedThread?.external_phone_number)}`
  }

  const handleMessageThreadSelected = (
    thread: MessageThread,
    messageGroup?: MessageGroup,
  ) => {
    setSelectedThread(thread)
    setSelectedMessageGroup(messageGroup)
  }

  const handleEditThread = () => {
    setEditThreadForm((prevEditThreadForm) => ({
      ...prevEditThreadForm,
      isEditing: false,
    }))

    if (selectedThread && newThreadName) {
      renameThread({
        threadId: selectedThread.id,
        name: newThreadName,
      })
    }
    window.gtag('event', 'thread_rename_request', {})
  }

  const getMessageThreadViewOrUnselected = () => {
    let threadHeader: ReactNode
    let threadView: ReactNode

    if (selectedThread) {
      if (!messagingEnabled) {
        threadHeader = <Header />
        threadView = (
          <MangoPlusAds
            onUpgrade={() => {
              window.gtag('event', 'messaging_upgrade_button_clicked', {})
            }}
          />
        )
      } else {
        threadHeader = (
          <MessageThreadHeader
            currentThreadName={selectedThread?.name}
            messageGroupName={selectedMessageGroup?.name}
            subheadingText={getSubheadingText()}
            isEditing={isEditing}
            newThreadName={newThreadName}
            onChangeThreadName={(newThreadName) => {
              setEditThreadForm((prevEditThreadForm) => ({
                ...prevEditThreadForm,
                newThreadName,
              }))
            }}
            onSubmitNewThreadName={handleEditThread}
            onCancelEdit={() => {
              setEditThreadForm((prevEditThreadForm) => ({
                ...prevEditThreadForm,
                isEditing: false,
              }))
            }}
          />
        )
        threadView = (
          <MessageThreadViewWrapper>
            <MessageThreadView currentThread={selectedThread} />
          </MessageThreadViewWrapper>
        )
      }
    } else {
      threadHeader = null
      threadView = (
        <EmptyDetailView message={t('messagingPage.emptyDetailViewMessage')} />
      )
    }

    return { threadHeader, threadView }
  }
  const getMessageQuickActions = () => {
    const quickActions: JSX.Element[] = []
    quickActions.push(
      <Edit
        key="edit"
        onClick={() => {
          setEditThreadForm((prevEditThreadForm) => ({
            ...prevEditThreadForm,
            isEditing: true,
          }))
        }}
      />,
    )
    if (selectedThread?.external_phone_number) {
      quickActions.push(
        <NewCall
          key="call"
          onClick={() => {
            const number = getValidNumber(selectedThread.external_phone_number)

            if (number.length < 10) {
              showErrorToast({
                message: t('messagingPage.errorToastMessage'),
              })
              return
            }

            window.gtag('event', 'quick_action_c2c_from_text', {})

            setCallModal({ open: true, callee: number })
          }}
        />,
      )

      if (canBlockNumber) {
        quickActions.push(
          <ToggleBlockedNumberButton
            key="toggle-blocked"
            phoneNumber={selectedThread.external_phone_number}
          />,
        )
      }
    }
    return quickActions
  }

  const { threadHeader, threadView } = getMessageThreadViewOrUnselected()

  return (
    <ResponsivePage
      listPanel={
        <MessageThreadListView
          onMessageThreadSelected={handleMessageThreadSelected}
        />
      }
      detailPanel={threadView}
      header={threadHeader}
      quickActionButtons={getMessageQuickActions()}
      listPanelAlwaysOpen
    />
  )
}

export default MessagingPage
